import { Timestamp, type DocumentData } from 'firebase/firestore';

/** Shape of a `campaigns` Firestore doc as returned by the SDK. */
export interface CampaignDoc {
  id: string;
  title: string;
  body: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  active: boolean;
  slug: string | null;
  excerpt: string | null;
  contentHtml: string | null;
  heroImageUrl: string | null;
  publishedAt: Timestamp | null;
  linkUrl: string | null;
}

/** Normalizes a raw Firestore value into a Timestamp, or null when it isn't one. */
const toTimestampOrNull = (value: unknown): Timestamp | null => {
  if (value instanceof Timestamp) {
    return value;
  }
  if (value && typeof value === 'object' && 'seconds' in value) {
    const { seconds, nanoseconds } = value as { seconds: unknown; nanoseconds?: unknown };
    if (typeof seconds === 'number') {
      return new Timestamp(seconds, typeof nanoseconds === 'number' ? nanoseconds : 0);
    }
  }
  return null;
};

const toStringOrDefault = (value: unknown, fallback: string): string => (typeof value === 'string' ? value : fallback);

const toStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

const CAMPAIGN_SEVERITIES: CampaignDoc['severity'][] = ['info', 'success', 'warning', 'error'];

const toSeverity = (value: unknown): CampaignDoc['severity'] =>
  CAMPAIGN_SEVERITIES.includes(value as CampaignDoc['severity']) ? (value as CampaignDoc['severity']) : 'info';

/**
 * Builds a CampaignDoc from a raw Firestore doc.
 *
 * `campaigns` docs are hand-edited in the Firestore console, so the unchecked cast
 * can't be trusted: a `publishedAt` entered as a string or number is truthy and throws
 * "toMillis is not a function" mid-render, and a `title`/`excerpt`/`heroImageUrl`/etc.
 * entered as a map passes straight through an unchecked spread and throws "Objects are
 * not valid as a React child". Neither What's New route sits behind an error boundary,
 * so either one white-screens the whole app shell. Narrowing every field here means a
 * malformed value reads as "missing" and the existing null/empty paths handle it — same
 * posture as the `app_downloads/current` type guards.
 */
export const toCampaignDoc = (id: string, data: DocumentData): CampaignDoc => ({
  id,
  title: toStringOrDefault(data.title, ''),
  body: toStringOrDefault(data.body, ''),
  severity: toSeverity(data.severity),
  active: data.active === true,
  slug: toStringOrNull(data.slug),
  excerpt: toStringOrNull(data.excerpt),
  contentHtml: toStringOrNull(data.contentHtml),
  heroImageUrl: toStringOrNull(data.heroImageUrl),
  publishedAt: toTimestampOrNull(data.publishedAt),
  linkUrl: toStringOrNull(data.linkUrl),
});
